import { useEffect, useLayoutEffect, useMemo } from "react";
import { debounceTime, Subject } from "rxjs";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  CancellationToken,
  QueryLoggingItem,
  ReportField,
  RunTileReportRequest,
  TileDataResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { getConditionsHash } from "../../../../utilities/gridStateHelper.ts";
import { isTileConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import type { TitleDataReturnType } from "./tileData.types.ts";
import useDataLoadingViewer from "./useDataLoadingViewer.ts";

interface Props {
  fields: ReportField[];
  requestConfig: RunTileReportRequest;
  runReport: (
    dataSetId: string,
    body: RunTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}
const useTileDataViewer = ({
  requestConfig,
  runReport,
  updateDevTools,
  setEtag,
  fields,
}: Props): TitleDataReturnType & { initialized: boolean } => {
  const subjectDelay = useMemo(() => new Subject<string>(), []);
  const { dimensions, refreshToken, conditions } = useBiCommonDataContext();
  const { data, isCalculating, error, calculate, failedMeasures, initialized } = useDataLoadingViewer({
    requestConfig,
    runReport,
    updateDevTools,
    setEtag,
  });

  const conditionsHash = useMemo(() => getConditionsHash(conditions, fields), [conditions, fields]);

  const configurationValidity = useMemo(() => {
    return isTileConfigurationValid(conditions, fields, dimensions);
  }, [conditions, fields, dimensions]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(calculate);
    return () => subscription.unsubscribe();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!configurationValidity.valid) {
      return;
    }
    subjectDelay.next(refreshToken ?? "");
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsHash, refreshToken, configurationValidity.valid]);

  return {
    error: error,
    configurationValidity,
    failedMeasures: failedMeasures,
    value: data?.value,
    delta: data?.delta,
    isLoading: isCalculating ?? false,
    period: data?.period,
    initialized,
  };
};

export default useTileDataViewer;
